<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient.js";
import { useRoute } from "vue-router";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import ManualSaveControls from "@/components/ManualSaveControls.vue";
import SearchContact from "@/components/SearchContact";
import TableList from "@/components/TableList.vue";
import useManualFormState from "@/composables/useManualFormState";
import dayjs from "dayjs";
var advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(advancedFormat);

const route = useRoute();
const breadcrumbStore = useBreadcrumbStore();
const contactId = route.params.id;
const contact = ref({});
const contactForm = ref({});
const tickets = ref([]);
const customers = ref([]);
const {
  editable: contactEditing,
  saving: contactSaving,
  isDirty: contactDirty,
  canSave: canSaveContact,
  statusText: contactStatusText,
  statusTone: contactStatusTone,
  sync: syncContactForm,
  startEditing: startEditingContact,
  cancelEditing: cancelEditingContact,
  startSaving: startSavingContact,
  markSaved: markContactSaved,
  markSaveFailed: markContactSaveFailed,
} = useManualFormState(contactForm, {
  leaveMessage: "You have unsaved contact changes. Leave this page?",
});

const buildContactForm = (value = {}) => ({
  name: value.name ?? "",
  email: value.email ?? "",
  phone: value.phone ?? "",
  addr: value.addr ?? "",
});

const contactDisplayName = computed(
  () => contactForm.value?.name || contact.value?.name || "Contact",
);

const loadContact = async () => {
  const response = await apiClient.get(`/api/contact/${contactId}`);
  contact.value = response.data;
  breadcrumbStore.setValue(contact.value.name);
  syncContactForm(buildContactForm(contact.value));
};

const loadTickets = async () => {
  const response = await apiClient.get(`/api/contact/${contactId}/tickets`);
  tickets.value = response.data;
};

const loadCustomers = async () => {
  const response = await apiClient.get(`/api/contact/${contactId}/customers`);
  customers.value = response.data;
};

onMounted(async () => {
  await loadContact();
  await Promise.all([loadTickets(), loadCustomers()]);
});

const save = async () => {
  if (!contactDirty.value) {
    cancelEditingContact();
    return;
  }

  startSavingContact();

  try {
    await apiClient.put(`/api/contact/${contact.value.id}`, {
      ...contactForm.value,
    });
    await loadContact();
    markContactSaved(buildContactForm(contact.value));
  } catch (error) {
    markContactSaveFailed(
      error?.response?.data?.message ?? "Unable to save contact changes.",
    );
  }
};

const addEmergencyContact = (newContact) => {
  apiClient.post(`/api/contact/${contact.value.id}/emergency-contact`, {
    emergency_contact_id: newContact.id,
  });
  contact.value.emergency_contacts.push(newContact);
};

const deleteEmergencyContact = async (newContact) => {
  try {
    // Send the DELETE request
    await apiClient.delete(
      `/api/contact/${contact.value.id}/emergency-contact/${newContact.id}`,
    );

    // If successful, remove the contact from the emergency_contacts array
    const index = contact.value.emergency_contacts.findIndex(
      (c) => c.id === newContact.id,
    );
    if (index !== -1) {
      contact.value.emergency_contacts.splice(index, 1);
    }
  } catch (error) {
    console.error("Failed to delete emergency contact:", error);
  }

  const viewTicket = (ticketId) => { };
};
</script>
<template>
  <div>
    <form-box :header="contactDisplayName">
      <template v-slot:nav>
        <ManualSaveControls
          :editing="contactEditing"
          :saving="contactSaving"
          :canSave="canSaveContact"
          :statusText="contactStatusText"
          :statusTone="contactStatusTone"
          @edit="startEditingContact"
          @cancel="cancelEditingContact"
          @save="save"
        />
      </template>
    </form-box>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <form-box header="Personal Details" v-if="contact?.id">
        <form class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl">
          <div>
            <label for="name">name</label>
            <TextField :data="contactForm.name" :editable="contactEditing" @update="(val) => (contactForm.name = val)" />
          </div>
          <div>
            <label for="email">Email</label>
            <TextField :data="contactForm.email" :editable="contactEditing" @update="(val) => (contactForm.email = val)" />
          </div>
          <div>
            <label for="phone">Phone</label>
            <TextField :data="contactForm.phone" :editable="contactEditing" @update="(val) => (contactForm.phone = val)" />
          </div>
          <div>
            <label for="contact_address">Address</label>
            <textarea class="w-full" id="contact_address" v-model="contactForm.addr" :disabled="!contactEditing"></textarea>
          </div>
        </form>
      </form-box>

      <form-box header="Emergency Contacts">
        <SearchContact @selected="addEmergencyContact" />
        <TableList v-if="contact.emergency_contacts && contact.emergency_contacts.length"
          :modelValue="contact.emergency_contacts" :cols="[
            { name: 'Name', row: 'name', editable: true },
            { name: 'Email', row: 'email', editable: true },
            { name: 'Phone', row: 'phone', editable: true },
          ]" :deletable="true" :box="false" @delete="deleteEmergencyContact" />
      </form-box>
    </div>
    <br />
    <form-box header="Customers">
      <table class="box list">
        <tbody>
          <tr v-for="customer in customers" :key="customer.id">
            <td><router-link :to="'/customer/' + customer.id">{{ customer.company ?? "No Company Name" }}</router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </form-box>
    <br>
    <form-box header="Tickets">
      <table class="box list">
        <tbody>
          <tr v-for="ticket in tickets" :key="ticket.id">
            <td><router-link :to="'/tickets/' + ticket.id">{{ ticket.subject }}</router-link></td>
          </tr>
        </tbody>
      </table>
    </form-box>
  </div>
</template>
